/*
 *
 * Copyright 2021-2024, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */

import React from 'react';
import ReactDOM from "react-dom";
import { Button, Alert } from "@blueprintjs/core";
import { Card, Elevation } from "@blueprintjs/core";
import { Callout, Icon, Colors } from "@blueprintjs/core";
import { Intent } from "@blueprintjs/core/lib/esm/index";
import { IconNames } from "@blueprintjs/icons";
import { Classes, Tooltip2 } from "@blueprintjs/popover2";
import { WrcForm } from "./WrcForm.js"
import { WrcImageMap } from "./WrcImageMap.js"
import { WrcServer } from "./WrcServer.js";
import { WrcDataTable } from "./WrcDataTables.js";
import WrcFormAggregate from "./WrcFormAggregate.js";

export class WrcDetailContent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            is_loaded: false,
            // grandchild is passed true/false and not the parent props
            readonly_if: this.props.level == 'child' ? this.checkReadOnlyIf(this.props) : this.props.readonly_if
        };
        this.clickNewElement = this.clickNewElement.bind(this)
    }

    componentDidMount() {
        this.setState({ is_loaded: true });
    }

    checkReadOnlyIf(context) {
        if (context.readonly_if === undefined || context.readonly_if === null) {
            return false;
        }
        let field = context[context.readonly_if.field_name]
        let is_readonly = context.readonly_if.values.every(function(value) {
            if (value === '*') {
                return (field !== undefined ||
                        field !== null ||
                        field !== '' ||
                        (Array.isArray(field) && field.length > 0)
                );
            }
            if (value === 'none') {
                return (field === undefined);
            }
            if (Array.isArray(field)){
                return (!field.includes(value));
            } else {
                return (value === field);
            }
        });
        return is_readonly
    }

    unpack = (elem_condensed) => {
        let elem_expanded;
        if (Array.isArray(elem_condensed)) {
            elem_expanded = [];
            for (let i = 0; i < elem_condensed.length; i++) {
                if (elem_condensed[i].table_name !== undefined) {
                    elem_expanded.push(elem_condensed[i]);
                }
                if (Array.isArray(elem_condensed[i].subtabs)) {
                    for (let j = 0; j < elem_condensed[i].subtabs.length; j++) {
                        if (elem_condensed[i].subtabs[j].table_name !== undefined) {
                            elem_expanded.push(elem_condensed[i].subtabs[j]);
                        }
                    }
                }
            }
        }
        return elem_expanded;
    }

    clickNewElement = (element) => {
        var default_fields = undefined;
        if (element.default_fields !== undefined) {
            default_fields = element.default_fields.map(function (field) {
                let default_value = undefined;
                if ('value' in field) {
                    default_value = field.value;
                } else if ('source' in field) {
                    if (this.props.level === 'child') {
                        default_value = this.props.list_data[this.props.selected_item][field.source];
                    } else if (this.props.level === 'grandchild') {
                        default_value = this.props.child_list_data[element.references][this.props.selected_grandchild['instance_number'] - 1][field.source];
                    }
                }
                return ({ name: field.name, value: default_value });
            }.bind(this));
        }
        ReactDOM.render(
            <WrcForm
                form_id={element.form_id}
                refresh={this.props.refresh}
                default_fields={default_fields}
                fkey={this.props.level === 'child' ? this.props.record_id : this.props.fkey}
                component_index={this.props.component_index} />,
            document.querySelector('#form'));
    }

    clickShowDescendant = (row) => {
        let content_list_data = (this.props.level === 'child' ? this.props.child_list_data : this.props.grandchild_list_data);
        if (this.props.selected_grandchild !== undefined && this.props.selected_grandchild['instance_number'] === (parseInt(row) + 1)) {
            this.props.grandChildCallback({
                selected_grandchild: undefined,
                grandchild_list_data: undefined,
                current_grandchild_tab: 'tab1'
            })
        }
        else {
            let current_element_tab = (this.props.use_aggregate_tab ? this.props.aggregate_tab : (this.props.level === 'child' ? this.props.current_tab : this.props.current_grandchild_tab));
            let index = parseInt(current_element_tab.substring(3)) - this.props.subtraction;
            let element = this.props.elements[index];
            let parent_id = content_list_data[element.table_name][row]['id'];
            let new_selected_grandchild = {};
            new_selected_grandchild['parent_id'] = parseInt(parent_id);
            new_selected_grandchild['instance_number'] = parseInt(row) + 1;
            new_selected_grandchild['references'] = element.table_name;
            new_selected_grandchild['table_schema'] = element.table_schema;
            new_selected_grandchild['record_id'] = this.props.record_id;
            this.getNewDescendantData(new_selected_grandchild);
        }
    }

    getNewDescendantData = (newDescendant) => {
        this.props.selectGrandChild(newDescendant, this.state.readonly_if)
    }

    clickEditElement = (e, row) => {
        let content_list_data = (this.props.level === 'child' ? this.props.child_list_data : this.props.grandchild_list_data);
        let current_element_tab = (this.props.use_aggregate_tab ? this.props.aggregate_tab : (this.props.level === 'child' ? this.props.current_tab : this.props.current_grandchild_tab));
        let index = parseInt(current_element_tab.substring(3)) - this.props.subtraction;
        let element = this.props.elements[index];
        let form_id = element.form_id;
        let local_record_id = content_list_data[element.table_name][row]['id'];
        ReactDOM.render(
            <WrcForm
                form_id={form_id}
                refresh={this.props.refresh}
                fkey={this.props.level === 'child' ? this.props.record_id : this.props.fkey}
                record_id={local_record_id}
                readonly={this.props.readonly ? this.props.readonly : this.state.readonly_if ? this.state.readonly_if : false}
                component_index={this.props.component_index} />,
            document.querySelector('#form'));
    }

    confirmElementDelete = (index, element, record_id_delete, row) => {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
        WrcServer.post('delete-record',
            { table_schema: this.props.table_schema, table_name: element.table_name, id: record_id_delete },
            (content) => this.afterDeleteElement(record_id_delete, row)
        );
    }

    clickDeleteElement = (row) => {
        let content_list_data = (this.props.level === 'child' ? this.props.child_list_data : this.props.grandchild_list_data);
        let current_element_tab = (this.props.use_aggregate_tab ? this.props.aggregate_tab : (this.props.level === 'child' ? this.props.current_tab : this.props.current_grandchild_tab));
        let index = parseInt(current_element_tab.substring(3)) - this.props.subtraction;
        let element = this.props.elements[index];
        let local_record_id = content_list_data[this.props.table_name][row]['id'];
        let deleteAlert = (
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
                isOpen={true}
                intent={Intent.DANGER}
                icon={IconNames.WARNING_SIGN}
                onCancel={() => this.cancelAlert()}
                onConfirm={() => this.confirmElementDelete(index, element, local_record_id, row)}>
                <p> Are you sure you wish to delete this record? </p>
            </Alert>
        );
        ReactDOM.render(deleteAlert, document.querySelector('#alertdiv'));
    }

    // parent callback to remove from data?
    afterDeleteElement = (record_id_delete, row) => {
        let postDeleteState = {};
        if (this.props.level === 'child' && this.props.selected_grandchild !== undefined) {
            // only reset grandchild if it it related to the deleted child record
            if (parseInt(this.props.selected_grandchild.parent_id) === parseInt(record_id_delete)) {
                postDeleteState = {
                    selected_grandchild: undefined,
                    current_grandchild_tab: undefined,
                    grandchild_list_data: undefined
                };
            } else if (parseInt(this.props.selected_grandchild.parent_id) > parseInt(record_id_delete)) {
                // if deleted record was above the one currently open with grandchild display,
                // need to decrement the current instance number
                let new_selected_grandchild = { ...this.props.selected_grandchild, instance_number: (this.props.selected_grandchild.instance_number - 1) }
                postDeleteState = {
                    selected_grandchild: new_selected_grandchild
                };
            }
            this.props.selectGrandChild(postDeleteState)
        }
        this.props.refresh();
    }

    cancelAlert() {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
    }

    displaySwitch = () => {
        let content_list_data = (this.props.level === 'child' ? this.props.child_list_data : this.props.grandchild_list_data);
        let descendant_button = (<td></td>);
        let edit_button = (<td></td>);
        let delete_button = (<td></td>);
        let add_button = (<td></td>);
        let no_record_message = '';
        switch (this.props.display_type) {
            case "table":
                let height = (this.props.level === 'child' ? (this.props.height - 98) : (this.props.height - 162));
                if (this.props.selected_element.hide_add !== true &&
                    (this.props.selected_element.max_records === undefined || content_list_data === undefined || content_list_data === null || content_list_data[this.props.selected_element.table_name] === undefined ||
                        (content_list_data[this.props.selected_element.table_name] !== undefined && content_list_data[this.props.selected_element.table_name].length < this.props.selected_element.max_records)) &&
                    this.props.readonly !== true && this.state.readonly_if !== true) {
                    add_button = (
                        <Button icon={IconNames.ADD}
                            intent={Intent.PRIMARY}
                            text='Add'
                            onClick={() => this.clickNewElement(this.props.selected_element)}
                            className='add-button'
                            style={{ marginBottom: '10px' }}
                        />
                    );
                    no_record_message = 'Click the Add button to create a record.';
                }
                let hidden_columns = [];
                let element_list = (<Callout intent={Intent.WARNING} title='No records' style={{ marginBottom: 10, minWidth: 500 }}>{no_record_message}</Callout>);
                if (this.props.selected_element !== undefined && content_list_data !== null && content_list_data !== undefined && content_list_data[this.props.selected_element.table_name] !== undefined && content_list_data[this.props.selected_element.table_name].length > 0) {
                    let element_list_headers = [];
                    element_list_headers = this.props.selected_element.columns.map(function (column) {
                        let column_class = column.class_name || '';
                        let column_style = typeof (column.style) === 'object' ? column.style : {};
                        if (column.show_in_list === false) {
                            return '';
                        } else {
                            return (<th className={column_class} style={column_style}>{column.header}</th>);
                        }
                    }.bind(this));
                    let table_list_data = [];
                    table_list_data = content_list_data[this.props.selected_element.table_name].map(function (record, index) {
                        let fields = this.props.selected_element.columns.map(function (column) {
                            let column_class = column.class_name || '';
                            let column_style = typeof (column.style) === 'object' ? column.style : {};
                            if (column.show_in_list === false) {
                                return '';
                            } else {
                                let display_value = record[column.field];
                                if (display_value && column.element_type === 'datestamp') {
                                    display_value = (
                                        <Tooltip2 className={Classes.TOOLTIP2_INDICATOR} content={display_value} intent={Intent.PRIMARY} placement="right">
                                            {display_value.substring(0, 10)}
                                        </Tooltip2>);
                                }
                                if (display_value === true) display_value = 'Yes';
                                if (display_value === false) display_value = 'No';            
                                return (<td className={column_class} style={column_style}>{display_value}</td>);
                            }
                        }.bind(this));
                        let disable_details = false;
                        if (Array.isArray(this.props.selected_element.disable_details)) {
                            this.props.selected_element.disable_details.forEach((condition) => {
                                let ind = (condition.values.includes(record[condition.field_name]) || record[condition.field_name] === null);
                                disable_details = disable_details || ind;
                            }); 1
                        }
                        if (Array.isArray(this.props.descendants) && this.props.level === 'child') {
                            if (!disable_details) {
                                let d_folder_ind = this.props.selected_grandchild;
                                descendant_button =
                                    (<td style={{ textAlign: 'center' }}>
                                        <Button
                                            icon={d_folder_ind ? (d_folder_ind.instance_number === (index + 1) ? IconNames.FOLDER_OPEN : IconNames.FOLDER_CLOSE) : IconNames.FOLDER_CLOSE}
                                            text={''}
                                            minimal={true}
                                            active={d_folder_ind ? (d_folder_ind === (index + 1) ? true : false) : false}
                                            onClick={() => this.clickShowDescendant(index)} />
                                    </td>);
                            } else {
                                descendant_button =
                                    (<td style={{ textAlign: 'center' }}>
                                        <Icon icon="disable" color={Colors.GRAY1} />
                                    </td>);
                            }
                        }
                        if (this.props.selected_element.hide_edit !== true) {
                            edit_button = (
                                <td style={{ textAlign: 'center' }}>
                                    <Button icon={IconNames.EDIT}
                                        text={''}
                                        minimal={true}
                                        onClick={(e) => this.clickEditElement(e, index)} />
                                </td>
                            );
                        }
                        if (this.props.selected_element.hide_delete !== true && this.props.readonly !== true && this.state.readonly_if !== true) {
                            delete_button = (
                                <td style={{ textAlign: 'center' }}>
                                    <Button icon={IconNames.DELETE}
                                        text={''}
                                        minimal={true}
                                        onClick={() => this.clickDeleteElement(index)} />
                                </td>
                            );
                        }
                        return (
                            <tr>
                                <td>
                                    {parseInt(index) + 1}
                                </td>
                                {fields}
                                {descendant_button}
                                {edit_button}
                                {delete_button}
                            </tr>);
                    }.bind(this));

                    let show_details = Array.isArray(this.props.selected_element.children) ? <th class="nosort" style={{ textAlign: 'center' }} id='show_details'>{this.props.selected_element.detail_header || 'Details'}</th> : <th id='show_details'></th>;
                    if (!Array.isArray(this.props.selected_element.children)) {
                        hidden_columns.push('#show_details');
                    }
                    let show_edit = this.props.selected_element.hide_edit ? <th id='show_edit'></th> : <th class="nosort" style={{ textAlign: 'center' }} id='show_edit'>View{this.props.readonly || this.state.readonly_if ? "" : "/Edit"}</th>;
                    if (this.props.selected_element.hide_edit) {
                        hidden_columns.push('#show_edit');
                    }
                    let show_delete = this.props.selected_element.hide_delete ? <th id='show_delete'></th> : <th class="nosort" style={{ textAlign: 'center' }} id='show_delete'>Delete</th>;
                    if (this.props.selected_element.hide_delete || this.props.readonly || this.state.readonly_if) {
                        hidden_columns.push('#show_delete');
                    }
                    element_list = (
                        <div>
                            <WrcDataTable
                                index={this.props.component_index}
                                entity={this.props.selected_element.title}
                                page_length={this.props.page_length}
                                show_search={this.props.selected_element.show_search}
                                className={this.props.class_name}
                                noWrapHeader={false}
                                height={height}
                                style={{ width: '100%' }}
                                level={this.props.level}
                                hidden_columns={hidden_columns}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        {element_list_headers}
                                        {show_details}
                                        {show_edit}
                                        {show_delete}
                                    </tr>
                                </thead>
                                <tbody>
                                    {table_list_data}
                                </tbody>
                            </WrcDataTable>
                            {/*
                                <table>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            {element_list_headers}
                                            {show_details}
                                            {show_edit}
                                            {show_delete}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {table_list_data}
                                    </tbody>
                                </table>
                            */}
                        </div>
                    );
                }
                return (
                    <Card interactive={false} elevation={Elevation.ZERO} className='wd-card-m-top' style={{ height: height, overflowY: 'auto' }}>
                        {element_list}
                        {add_button}
                    </Card>
                );
            case "map":
                return (
                    <div>
                        <WrcImageMap
                            height={this.props.selected_element.height}
                            image_source={this.props.selected_element.image_source}
                            image_description={this.props.selected_element.image_description}
                            map_name={this.props.selected_element.map_name}
                            width={this.props.selected_element.width}
                            areas={this.props.selected_element.areas}
                            marker_name={this.props.selected_element.marker_name}
                            columns={this.props.selected_element.columns}
                            form_id={this.props.selected_element.form_id}
                            data={content_list_data[this.props.selected_element.table_name]}
                            table_schema={this.props.table_schema}
                            table_name={this.props.selected_element.table_name}
                            references={this.props.references}
                            fkey={this.props.record_id}
                            refresh={this.props.refresh}
                            level={this.props.level}
                            component_index={this.props.component_index}
                        />
                    </div>
                );
            case "aggregate":
                return (
                    <div>
                        <WrcFormAggregate
                            program_role={this.props.program_role}
                            readonly_if={this.props.readonly_if}
                            checkReadOnlyIf={this.props.checkReadOnlyIf}

                            height={((this.props.selected_element.agg_tab_direction === 'horizontal') ?
                                this.props.height - 100 :
                                (this.props.level === 'child' ? this.props.height - 98 : this.props.height - 162))}
                            agg_text_align={this.props.selected_element.agg_text_align}
                            agg_tab_direction={this.props.selected_element.agg_tab_direction}
                            tab_name={this.props.selected_element.tab_name}
                            subtabs={this.props.selected_element.subtabs}
                            component_index={this.props.component_index}
                            refresh={this.props.refresh}
                            table_schema={this.props.table_schema}
                            references={this.props.references}
                            page_length={this.props.page_length}
                            class_name={this.props.class_name}
                            level={this.props.level}
                            fkey={this.props.fkey}
                            selected_element={this.props.selected_element}
                            elements={this.props.elements}
                            index={this.props.index}
                            subtraction={2}
                            readonly={this.props.readonly}
                            selectGrandChild={this.props.selectGrandChild}
                            selected_grandchild={this.props.selected_grandchild}
                            grandChildCallback={this.props.grandChildCallback}
                            parentCallback={this.props.parentCallback}
                            selected_item={this.props.selected_item}
                            parentSetState={this.props.parentSetState}
                            parentGetState={this.props.parentGetState}
                            list_data={this.props.list_data}
                            child_list_data={this.props.child_list_data}
                            grandchild_list_data={this.props.grandchild_list_data}
                            current_tab={this.props.current_tab}
                            current_grandchild_tab={this.props.current_grandchild_tab}
                            record_id={this.props.record_id}
                        />
                    </div>
                );
            default:
                return (<div></div>);
        }
    }

    render() {
        let height = (this.props.level === 'child' ? (this.props.height - 98) : (this.props.height - 162));
        return (this.state.is_loaded ?
            this.displaySwitch() :
            (<div>
                <Card style={{ height: height }}><h3>Loading...</h3></Card>
            </div>)
        );
    }
}

export default WrcDetailContent;
